import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, CheckCircle, XCircle, AlertTriangle, Settings, Bot } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getEdgeFunctionUrl } from '@/utils/supabaseUrl';
import { WebhookInfo } from './types';

export function CoordinatorBotWebhookManager() {
  const [webhookInfo, setWebhookInfo] = useState<WebhookInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const { toast } = useToast();

  const checkWebhookStatus = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('telegram-coordinator-bot-setup', {
        body: { action: 'webhook' }
      });

      if (error) {
        console.error('Error checking coordinator bot webhook:', error);
        toast({
          title: "Error",
          description: "Failed to check webhook status",
          variant: "destructive",
        });
        return;
      }

      setWebhookInfo(data?.webhook?.result || null);
    } catch (error) {
      console.error('Error calling webhook check function:', error);
      toast({
        title: "Error",
        description: "Failed to check webhook status",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setupWebhook = async () => {
    setIsSettingUp(true);
    try {
      const { data, error } = await supabase.functions.invoke('telegram-coordinator-bot-setup', {
        body: { action: 'setup' }
      });

      if (error) {
        console.error('Error setting up coordinator bot webhook:', error);
        toast({
          title: "Error",
          description: "Failed to set up webhook",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Coordinator bot webhook set up successfully",
      });

      // Refresh status after setup
      await checkWebhookStatus();
    } catch (error) {
      console.error('Error calling webhook setup function:', error);
      toast({
        title: "Error", 
        description: "Failed to set up webhook",
        variant: "destructive",
      });
    } finally {
      setIsSettingUp(false);
    }
  };

  useEffect(() => {
    checkWebhookStatus();
  }, []);

  const getStatusBadge = () => {
    if (!webhookInfo) {
      return <Badge variant="secondary">Unknown</Badge>;
    }

    if (webhookInfo.url && webhookInfo.url.includes('telegram-coordinator-bot')) {
      return <Badge variant="default" className="bg-green-500">Active</Badge>;
    }

    return <Badge variant="destructive">Inactive</Badge>;
  };

  const getStatusAlert = () => {
    if (!webhookInfo) {
      return (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Unable to determine webhook status. Click "Check Status" to retry.
          </AlertDescription>
        </Alert>
      );
    }

    if (webhookInfo.url && webhookInfo.url.includes('telegram-coordinator-bot')) {
      return (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Coordinator bot webhook is properly configured and active.
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <Alert variant="destructive">
        <XCircle className="h-4 w-4" />
        <AlertDescription>
          Coordinator bot webhook is not configured. Click "Setup Webhook" to configure it.
        </AlertDescription>
      </Alert>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          Coordinator Bot Webhook
        </CardTitle>
        <CardDescription>
          Manage the webhook configuration for the Telegram coordinator bot
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {getStatusAlert()}

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Webhook Status</Label>
              <div className="flex items-center gap-2 mt-1">
                {getStatusBadge()}
                {webhookInfo?.pending_update_count !== undefined && (
                  <Badge variant="outline">
                    {webhookInfo.pending_update_count} pending updates
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {webhookInfo?.url && (
            <div>
              <Label className="text-sm font-medium">Current Webhook URL</Label>
              <Input 
                value={webhookInfo.url} 
                readOnly 
                className="mt-1 font-mono text-sm"
              />
            </div>
          )}

          <div>
            <Label className="text-sm font-medium">Expected Webhook URL</Label>
            <Input 
              value={getEdgeFunctionUrl('telegram-coordinator-bot')} 
              readOnly 
              className="mt-1 font-mono text-sm bg-muted"
            />
          </div>

          {webhookInfo?.last_error_message && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium">Last Error:</div>
                <div className="text-sm mt-1">{webhookInfo.last_error_message}</div>
                {webhookInfo.last_error_date && (
                  <div className="text-xs mt-1 text-muted-foreground">
                    {new Date(webhookInfo.last_error_date * 1000).toLocaleString()}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={checkWebhookStatus} 
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Check Status
          </Button>
          
          <Button 
            onClick={setupWebhook} 
            disabled={isSettingUp}
            className="flex items-center gap-2"
          >
            <Settings className={`h-4 w-4 ${isSettingUp ? 'animate-spin' : ''}`} />
            Setup Webhook
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}